<template>
	<div class='knd-audio-player knd-wrapper' ref="audioContainer">
    <div class="audio-player-container">
      <div class="play-icon animation-icon" @click="onClick">
        <PlayIcon :state="playState" />
      </div>
      <span id="current-time" class="time">{{ currentTime }}</span>
      <input 
        type="range" 
        id="seek-slider" 
        ref="seekslider"
        :max="seekSliderMax" 
        value="0"
      >
      <span id="duration" class="time">{{audioDuration}}</span>

      <div class="volume animation-icon" @click="onClickVolumeIcon">
        <MuteIcon :muted="muted" />
      </div>

      <audio ref='audioplayer' crossorigin="" :muted="muted"></audio>
    </div>
	</div>
</template>

<script>

import { useStore } from 'vuex'
import { ref, onMounted, watch } from 'vue';

import MuteIcon from '@/components/icons/mute';
import PlayIcon from '@/components/icons/play';

export default {
  props: ['src', 'muted', 'onClickVolume', 'playSound'],
  components: {MuteIcon, PlayIcon},
  setup (props) {
    const store = useStore()
    let audioContainer = ref(null);
    let seekslider = ref(null);
    let audioplayer = ref(null);

    let audio;
    let audioDuration = ref('0:00');
    let seekSliderMax = ref(0);
    let currentTime = ref(undefined);
    let rAF = null;

    let STATES = {
      PLAY: 'play',
      PAUSE: 'pause',
      REWIND: 'rewind'
    }

    let playState = ref(undefined);

    watch( () => props.src, (newVal, oldVal)=> {
      if(newVal !== oldVal) {
        startup();
      }
    });

    watch( () => props.playSound, (newVal, oldVal)=> {
      if(newVal) {
        playAudio();
      } else {
        pauseAudio();
      }
    });

    onMounted(() => {
      startup();
    })

    const startup = () => {
      playState.value = STATES.PLAY;
      audio = audioplayer.value;

      audio.src = props.src;
      audio.currentTime = 0;
      currentTime.value = '0:00';
      seekslider.value.value = 0;
      showRangeProgress();

      seekslider.value.addEventListener('input', () => {
        currentTime.value = calculateTime(seekslider.value.value);
        showRangeProgress();

        if(!audio.paused) {
          cancelAnimationFrame(rAF);
        }
      });

      seekslider.value.addEventListener('change', () => {
        audio.currentTime = seekslider.value.value;

        if(!audio.paused) {
          requestAnimationFrame(whilePlaying);
        }
      });

      audio.addEventListener('timeupdate', () => {
        seekslider.value.value = Math.floor(audio.currentTime);
      });

      audio.onerror = function () { 
          alert("Cannot play audio file: "  
                      + this.error.message); 
      } 

      if (audio.readyState > 0) {
        displayDuration();
        setSliderMax();
      } else {
        audio.addEventListener('loadedmetadata', () => {
          displayDuration();
          setSliderMax();
        });
      }
    }

    const onClickVolumeIcon = () => {
      props.onClickVolume();
    }

    const setSliderMax = () => {
      seekSliderMax.value = Math.floor(audio.duration);
    }

    const calculateTime = (secs) => { 
      const minutes = Math.floor(secs / 60);
      const seconds = Math.floor(secs % 60);
      const returnedSeconds = seconds < 10 ? `0${seconds}` : `${seconds}`;
      return `${minutes}:${returnedSeconds}`;
    }

    const displayDuration = () => {
      audioDuration.value = calculateTime(audio.duration);
    }

    const onClick =() => {
      toggle();
    }

    const toggle = () => {
      if(playState.value === STATES.PLAY) {
        playAudio();
      } else {
        pauseAudio();
      }
    }

    const playAudio = () => {
      let promise = audio.play();

      if (promise !== undefined) {
        promise.then(_ => {
          requestAnimationFrame(whilePlaying);
          playState.value = STATES.PAUSE;
        }).catch(error => {
           console.log('lukt niet om te spelen ivm browser')
        });
    }

    }

    const pauseAudio = () => {
      audio.pause();
      cancelAnimationFrame(rAF);
      playState.value = STATES.PLAY;
    }

    const showRangeProgress = () => {
      audioContainer.value.style.setProperty('--seek-before-width', seekslider.value.value / seekslider.value.max * 100 + '%');
    }

    const whilePlaying = () => {
      seekslider.value.value = Math.floor(audio.currentTime);
      currentTime.value = calculateTime(seekslider.value.value);
      showRangeProgress();
      rAF = requestAnimationFrame(whilePlaying);
    }

    return {
      audioDuration, 
      currentTime,
      seekSliderMax,
      seekslider,
      onClick,
      onClickVolumeIcon,
      playState,
      STATES,
      audioContainer,
      audioplayer
    }
  }
}
</script>


<style lang="scss">



.knd-audio-player {
  // --buffered-width: 78.69822485207101%;
  --seek-before-width: 0%;

  --thumb-bg: #00D9AD;
  --thumb-bg-active: #00D9AD;
  --thumb-border-color: #00D9AD;

  --range-bg: #00D9AD;

  position: relative;

  .audio-player-container {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .play-icon {
    width: 24px;
    cursor: pointer;

    svg {
      pointer-events: none;
    }
  }

  .volume {
    cursor: pointer;
    width: 32px;
    height: 32px;

    svg {
      pointer-events: none;
    }

  }

  .time {
    font-size: 14px;
    width: 40px;
    
  }
  #current-time {
    margin-left: 0.5em;
  }

  #duration {
    margin-right: 0.5em;
  }

  input[type="range"] {
    position: relative;
    -webkit-appearance: none;
    width: 48%;
    margin: 0;
    padding: 0;
    height: 19px;
    // margin: 30px 2.5% 20px 2.5%;
    // float: left;
    outline: none;

    height: 4px;
    background: #d8d8d8;
    border-radius: 2px;    
  }
  input[type="range"]::-webkit-slider-runnable-track {
    width: 100%;
    height: 3px;
    cursor: pointer;
    background: linear-gradient(to right, rgba(0, 125, 181, 0.6) var(--buffered-width), rgba(0, 125, 181, 0.2) var(--buffered-width));
  }

  input[type="range"]::before {
    position: absolute;
    content: "";
    top: 0;
    left: 0;
    width: var(--seek-before-width);
    height: 4px;
    background-color: var(--range-bg);
    cursor: pointer;
  }

  input[type="range"]::-webkit-slider-thumb {
    position: relative;
    -webkit-appearance: none;
    box-sizing: content-box;
    border: 1px solid var(--thumb-border-color);
    height: 15px;
    width: 15px;
    border-radius: 50%;
    background-color: var(--thumb-bg);
    cursor: pointer;
    margin: -7px 0 0 0;
  }
  input[type="range"]:active::-webkit-slider-thumb {
    transform: scale(1.2);
    background: var(--thumb-bg-active);
  }
  input[type="range"]::-moz-range-track {
    width: 100%;
    height: 3px;
    cursor: pointer;
    background: linear-gradient(to right, rgba(0, 125, 181, 0.6) var(--buffered-width), rgba(0, 125, 181, 0.2) var(--buffered-width));
  }
  input[type="range"]::-moz-range-progress {
    background-color: var(--range-bg);
  }
  input[type="range"]::-moz-focus-outer {
    border: 0;
  }
  input[type="range"]::-moz-range-thumb {
    box-sizing: content-box;
    border: 1px solid var(--thumb-border-color);
    height: 15px;
    width: 15px;
    border-radius: 50%;
    background-color: var(--thumb-bg);
    cursor: pointer;
  }
  input[type="range"]:active::-moz-range-thumb {
    transform: scale(1.2);
    background: var(--thumb-bg-active);
  }
  input[type="range"]::-ms-track {
    width: 100%;
    height: 3px;
    cursor: pointer;
    background: transparent;
    border: solid transparent;
    color: transparent;
  }
  input[type="range"]::-ms-fill-lower {
    background-color: var(--range-bg);
  }
  input[type="range"]::-ms-fill-upper {
    background: linear-gradient(to right, rgba(0, 125, 181, 0.6) var(--buffered-width), rgba(0, 125, 181, 0.2) var(--buffered-width));
  }
  input[type="range"]::-ms-thumb {
    box-sizing: content-box;
    border: 1px solid var(--thumb-border-color);
    height: 15px;
    width: 15px;
    border-radius: 50%;
    background-color: var(--thumb-bg);
    cursor: pointer;
  }
  input[type="range"]:active::-ms-thumb {
    transform: scale(1.2);
    background: var(--thumb-bg-active);
  }
}
</style>